import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  CheckCircle2,
  Droplets,
  ShieldCheck,
  Building2,
  Activity,
  Target,
  Eye,
  Users,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import chlorination from "../images/chlorination.jpeg";

export default function AboutPage() {
  const [faqOpen, setFaqOpen] = useState(0);

  const coreValues = useMemo(
    () => [
      {
        title: "Engineering Excellence",
        text: "Every water treatment solution is designed with a strong focus on performance, durability, maintainability and long-term operational value.",
        icon: Sparkles,
      },
      {
        title: "Water Safety First",
        text: "We build systems that prioritize cleaner, bacteria-safe and confidence-inspiring water for residents, developers and facility managers.",
        icon: ShieldCheck,
      },
      {
        title: "Property-Centric Design",
        text: "Each installation is customized around occupancy, source water quality, architecture, utility layout and user expectations.",
        icon: Building2,
      },
      {
        title: "Smart Operations",
        text: "With monitoring, alerts and data-driven visibility, we help properties maintain better operational control and service responsiveness.",
        icon: Activity,
      },
    ],
    []
  );

  const milestones = useMemo(
    () => [
      {
        year: "Step 01",
        title: "Understanding the Property",
        text: "We begin with source-water analysis, site requirements, occupancy estimation and infrastructure assessment.",
      },
      {
        year: "Step 02",
        title: "Custom Water System Planning",
        text: "We define process design, capacity, treatment sequence and smart monitoring architecture around the project.",
      },
      {
        year: "Step 03",
        title: "Execution & Integration",
        text: "Our systems are integrated into the utility ecosystem with a focus on efficiency, aesthetics and reliability.",
      },
      {
        year: "Step 04",
        title: "Long-Term Support",
        text: "We support operations with monitoring, service planning and performance visibility for stable everyday water delivery.",
      },
    ],
    []
  );

  const team = useMemo(
    () => [
      {
        name: "Water Engineering Team",
        role: "Plant Design & Process Planning",
        text: "Focused on treatment logic, water quality adaptation and dependable plant architecture.",
      },
      {
        name: "Project Execution Team",
        role: "Installation & Integration",
        text: "Ensures smooth coordination of civil, piping, equipment and utility integration on-site.",
      },
      {
        name: "Monitoring & Service Team",
        role: "Operations Support",
        text: "Tracks system performance, service needs and smart monitoring workflows for ongoing reliability.",
      },
    ],
    []
  );

  const faqs = useMemo(
    () => [
      {
        q: "What makes AquaHome different from a standard water treatment vendor?",
        a: "AquaHome is positioned as a premium water infrastructure partner. We focus not only on treatment performance, but also on property-fit design, smart monitoring, user confidence and long-term value for residential developments.",
      },
      {
        q: "Do you support both new developments and existing buildings?",
        a: "Yes. AquaHome solutions can be planned for new projects as well as retrofit opportunities in existing apartment societies, villas, luxury residences and commercial-residential complexes.",
      },
      {
        q: "Can AquaHome solutions be adapted to different source water conditions?",
        a: "Yes. Systems are designed after understanding the source-water profile, whether the project uses borewell water, municipal water or a mixed supply condition.",
      },
      {
        q: "Is IoT or smart monitoring included in the system approach?",
        a: "Yes. AquaHome supports intelligent monitoring for operational visibility, helping properties track performance, maintenance needs and critical parameters more effectively.",
      },
      {
        q: "Who is AquaHome best suited for?",
        a: "AquaHome is best suited for apartment societies, high-rise towers, gated communities, luxury villas, mansions, townships and premium real estate developments seeking strong water quality positioning.",
      },
    ],
    []
  );

  return (
    <div className="overflow-x-hidden">
      <section className="relative mx-auto max-w-7xl px-6 pb-20 pt-20 lg:pt-24">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200">
              About AquaHome
            </div>

            <h1 className="text-4xl font-bold leading-tight md:text-6xl">
              Building Trust in{" "}
              <span className="text-cyan-400">Water Infrastructure</span> for
              Modern Properties
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              AquaHome delivers premium water treatment infrastructure for
              residential and real estate projects, combining engineering,
              property-specific design, water safety and smart operational
              visibility into one integrated solution.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-2xl bg-cyan-500 px-7 py-3.5 font-semibold text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:bg-cyan-400"
              >
                Get Proposal
                <ArrowRight size={18} />
              </Link>

              <Link
                to="/solutions"
                className="rounded-2xl border border-white/15 bg-white/5 px-7 py-3.5 font-semibold transition hover:bg-white/10"
              >
                Explore Solutions
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 36 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative"
          >
            <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 p-3 shadow-2xl backdrop-blur-xl">
              <div className="relative overflow-hidden rounded-[1.5rem]">
                <img
                  src={chlorination}
                  alt="AquaHome water treatment plant"
                  className="h-[460px] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/15 to-transparent" />

                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-sm uppercase tracking-[0.25em] text-cyan-300">
                    Premium Infrastructure
                  </p>
                  <h2 className="mt-3 text-2xl font-bold">
                    Clean Water Systems for Residential Excellence
                  </h2>
                  <p className="mt-2 max-w-xl text-sm leading-7 text-slate-300">
                    Designed to strengthen safety, operational confidence and
                    project value across premium residential environments.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo strip */}
      <section className="mx-auto max-w-7xl px-6 pb-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { src: "https://images.unsplash.com/photo-1513828583688-c52646db42da?auto=format&fit=crop&w=600&q=80", label: "High-Rise Integration" },
            { src: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=600&q=80", label: "Filtration Systems" },
            { src: "https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?auto=format&fit=crop&w=600&q=80", label: "Water Networks" },
            { src: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=600&q=80", label: "Township Projects" },
          ].map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative overflow-hidden rounded-2xl"
            >
              <img
                src={item.src}
                alt={item.label}
                className="h-44 w-full object-cover transition duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 to-transparent" />
              <p className="absolute bottom-3 left-3 text-xs font-semibold text-slate-200">{item.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.03]">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-20 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65 }}
            className="rounded-[2rem] border border-white/10 bg-slate-950/60 p-8 backdrop-blur-xl"
          >
            <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-500/10 text-cyan-300">
              <Target size={24} />
            </div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">
              Our Mission
            </p>
            <h2 className="mt-4 text-3xl font-bold">To Make Water Quality a Premium Everyday Experience</h2>
            <p className="mt-5 leading-8 text-slate-400">
              Our mission is to design and deliver high-quality water treatment
              infrastructure that improves safety, reliability and confidence in
              residential water systems. We aim to help buildings and communities
              move beyond basic compliance toward better living standards.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, delay: 0.08 }}
            className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-cyan-500/10 to-emerald-500/10 p-8"
          >
            <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-emerald-400/20 bg-emerald-500/10 text-emerald-300">
              <Eye size={24} />
            </div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-400">
              Our Vision
            </p>
            <h2 className="mt-4 text-3xl font-bold">Smarter, Safer Water Infrastructure for Every Modern Property</h2>
            <p className="mt-5 leading-8 text-slate-300">
              We envision a future where premium residential developments treat
              clean and intelligently managed water as a core part of resident
              experience, property quality and operational excellence.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65 }}
          className="mx-auto mb-14 max-w-3xl text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">
            Who We Are
          </p>
          <h2 className="mt-4 text-4xl font-bold md:text-5xl">
            Engineering-Driven Water Solutions for Residential Growth
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-400">
            AquaHome brings together water treatment expertise, practical
            implementation understanding and premium property thinking to create
            systems that work well technically and feel valuable commercially.
          </p>
        </motion.div>

        <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-4">
          {coreValues.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: index * 0.08 }}
                className="rounded-3xl border border-white/10 bg-white/5 p-7 backdrop-blur-xl"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-500/10 text-cyan-300">
                  <Icon size={24} />
                </div>
                <h3 className="text-2xl font-semibold">{item.title}</h3>
                <p className="mt-4 leading-7 text-slate-400">{item.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.03]">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65 }}
            className="mx-auto mb-14 max-w-3xl text-center"
          >
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-400">
              Our Process
            </p>
            <h2 className="mt-4 text-4xl font-bold md:text-5xl">
              How AquaHome Brings a Project to Life
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-400">
              A structured journey from site understanding to dependable,
              intelligently managed water delivery.
            </p>
          </motion.div>

          <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-4">
            {milestones.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: index * 0.08 }}
                className="rounded-3xl border border-white/10 bg-slate-950/60 p-7 backdrop-blur-xl"
              >
                <div className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-400">
                  {item.year}
                </div>
                <h3 className="mt-4 text-2xl font-semibold">{item.title}</h3>
                <p className="mt-4 leading-7 text-slate-400">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65 }}
          className="mx-auto mb-14 max-w-3xl text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">
            Team
          </p>
          <h2 className="mt-4 text-4xl font-bold md:text-5xl">
            The People Behind AquaHome
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-400">
            A multidisciplinary project team focused on design, implementation,
            monitoring and long-term service value.
          </p>
        </motion.div>

        <div className="grid gap-7 md:grid-cols-3">
          {team.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: index * 0.08 }}
              className="rounded-[2rem] border border-white/10 bg-white/5 p-7 text-center backdrop-blur-xl"
            >
              <div className="mx-auto mb-5 flex h-24 w-24 items-center justify-center rounded-full border border-cyan-400/20 bg-cyan-500/10 text-cyan-300">
                <Users size={34} />
              </div>
              <h3 className="text-2xl font-semibold">{member.name}</h3>
              <p className="mt-2 text-sm font-medium uppercase tracking-[0.18em] text-cyan-400">
                {member.role}
              </p>
              <p className="mt-4 leading-7 text-slate-400">{member.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65 }}
          className="mx-auto mb-14 text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">
            FAQ
          </p>
          <h2 className="mt-4 text-4xl font-bold md:text-5xl">
            Common Questions About AquaHome
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((item, index) => {
            const isOpen = faqOpen === index;
            return (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: index * 0.05 }}
                className="overflow-hidden rounded-2xl border border-white/10 bg-slate-950/60"
              >
                <button
                  onClick={() => setFaqOpen(isOpen ? -1 : index)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="text-lg font-semibold">{item.q}</span>
                  <ChevronDown
                    className={`transition ${isOpen ? "rotate-180" : ""}`}
                    size={20}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28 }}
                    >
                      <div className="px-6 pb-5 text-slate-400">{item.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-5xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65 }}
            className="rounded-[2rem] border border-white/10 bg-gradient-to-r from-cyan-500/10 via-slate-900/70 to-emerald-500/10 p-8 text-center md:p-12"
          >
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-500/10 text-cyan-300">
              <Droplets size={28} />
            </div>
            <h2 className="text-3xl font-bold md:text-4xl">
              Ready to Build a Better Water Experience?
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Let AquaHome help you design the right treatment infrastructure for
              your society, villa, mansion or real estate project.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/contact"
                className="rounded-2xl bg-cyan-500 px-7 py-3.5 font-semibold text-slate-950 transition hover:bg-cyan-400"
              >
                Talk to Our Team
              </Link>
              <Link
                to="/solutions"
                className="rounded-2xl border border-white/15 bg-white/5 px-7 py-3.5 font-semibold transition hover:bg-white/10"
              >
                View Solutions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}